import React, { useMemo } from 'react';
import moment from 'moment';
import { translate } from '../../../services/translate';
import { StyleProp, TextStyle, View } from 'react-native';
import Badge, { BadgeColor } from '../../../components/Badge';
import { VacancyStatus } from '../../../schemas/deac';

interface VacancyStatusProps {
    vacancy: VacancyForStatus;
    style?: StyleProp<TextStyle>;
    size?: 'small' | 'medium' | 'large';
}

export function getVacancyStatus({ vacancy, style, size = 'medium' }: VacancyStatusProps) {
    let customName = 'opened';
    let badgeColor: BadgeColor = 'orange';

    if (vacancy.deletedAt) {
        customName = 'canceled';
        badgeColor = 'red';
    } else if (vacancy.applicationsFinish && moment().isBetween(vacancy.applicationsFinish, vacancy.activityStart, null, '[)')) {
        customName = 'confirmed_f';
        badgeColor = 'yellow';
    } else if (moment().isBefore(vacancy.applicationsStart)) {
        customName = 'scheduled';
        badgeColor = 'blue';
    } else if (moment().isBetween(vacancy.activityStart, vacancy.activityFinish, null, '[)')) {
        customName = 'inProgress';
        badgeColor = 'purple';
    } else if (moment().isAfter(vacancy.activityFinish)) {
        customName = 'finished_f';
        badgeColor = 'green';
    }

    return <View style={[{ justifyContent: 'center' }, style]}>
        <Badge mode='SQUARE' size={size} color={badgeColor}>{translate(customName)}</Badge>
    </View>;
}

interface VacancySituation {
    isScheduled: boolean;
    isOpened: boolean;
    isConfirmed: boolean;
    isProgress: boolean;
    isFinished: boolean;
    isCanceled: boolean;
    isViewMode: boolean;
    status: VacancyStatus;
}

export const useVacancyStatus = (vacancy: Vacancy): VacancySituation => {
    return useMemo(() => {
        const now = moment();
        const isDeleted = vacancy.deletedAt !== null;

        if (isDeleted) {
            return {
                isScheduled: false,
                isOpened: false,
                isConfirmed: false,
                isProgress: false,
                isFinished: false,
                isCanceled: true,
                isViewMode: true,
                status: 'canceled',
            };
        }

        const isScheduled = now.isBefore(vacancy.applicationsStart);
        const isOpened = vacancy.applicationsFinish
            ? now.isBetween(vacancy.applicationsStart, vacancy.applicationsFinish, null, '[)')
            : now.isBetween(vacancy.applicationsStart, vacancy.activityStart, null, '[)');
        const isConfirmed = vacancy.applicationsFinish ? now.isBetween(vacancy.applicationsFinish, vacancy.activityStart, null, '[)') : false;
        const isProgress = now.isBetween(vacancy.activityStart, vacancy.activityFinish, null, '[)');
        const isFinished = now.isSameOrAfter(vacancy.activityFinish);

        const isViewMode = isProgress || isFinished;

        let status: VacancyStatus;
        if (isScheduled) {
            status = 'scheduled';
        } else if (isOpened) {
            status = 'opened';
        } else if (isConfirmed) {
            status = 'confirmed';
        } else if (isProgress) {
            status = 'progress';
        } else {
            status = 'finished';
        }

        return {
            isScheduled,
            isOpened,
            isConfirmed,
            isProgress,
            isFinished,
            isCanceled: false,
            isViewMode,
            status
        };
    }, [vacancy.deletedAt, vacancy.applicationsStart, vacancy.activityStart, vacancy.activityFinish]);
};

export function getApplicationsStatus(application: VacancyApplication) {
    let customName = 'finished_f';
    let badgeColor: BadgeColor = 'green';

    const vacancyStatus = useVacancyStatus(application.vacancy);

    if (application.deletedAt) {
        customName = 'canceled';
        badgeColor = 'red';
    } else if (vacancyStatus.isOpened) {
        customName = 'approved_singular';
        badgeColor = 'blue';
    } else if (vacancyStatus.isConfirmed) {
        customName = 'confirmed_f';
        badgeColor = 'yellow';
    } else if (vacancyStatus.isProgress) {
        customName = 'progress';
        badgeColor = 'purple';
    }

    return <View style={{ justifyContent: 'center' }}>
        <Badge mode='SQUARE' color={badgeColor}>{translate(customName)}</Badge>
    </View>;
}

export function getPresenceStatus(presence: Presence, forceStatus?: 'present' | 'absent') {
    let customName = 'pending';
    let badgeColor: BadgeColor = 'orange';

    if (forceStatus) {
        if (forceStatus === 'present') {
            customName = 'confirmed';
            badgeColor = 'green';
        } else if (forceStatus === 'absent') {
            customName = 'absent';
            badgeColor = 'red';
        }
    } else if (presence.present === true) {
        customName = 'confirmed';
        badgeColor = 'green';
    } else if (presence.present === false) {
        customName = 'absent';
        badgeColor = 'red';
    }

    return <View style={{ justifyContent: 'center' }}>
        <Badge mode='SQUARE' color={badgeColor}>{translate(customName)}</Badge>
    </View>;
}
